import { QuestionCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { get, isNil } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { logger } from '@/infrastructure/logger';
import { PAGE_ID } from '@/infrastructure/reporter/datong/id';
import { toArray } from '@/infrastructure/utils/to-array';
import { getWildcardOperator } from '@/presentation/components/exception-form/utils';
import { VersionType } from '@/presentation/components/form/components/version-select';
import { FormEntry, FormType } from '@/presentation/components/form/index';
import { FilterEntry } from '@/presentation/components/form/types';
import { useStores } from '@/presentation/store';
import {
  AppVersionOperator,
  ExOption,
  FIELD_ALL,
  FieldKey,
  FieldScope,
  getLocalField,
  Option,
  VersionFilter,
} from '@/proto/field';
import { IssueType } from '@/proto/issue';
import { TimeType } from '@/proto/metric';
import { toSnakeCase } from '@raft/utils/src/case/snake-case';
import { MatchMode } from './standards/types';

type AppVersionType = Omit<VersionType, 'name'>;

export const isEqualVersionFilter = (filter: string) => filter === VersionFilter.Or;

export const isGeLeVersionFilter = (filter: string) => filter === VersionFilter.Ge
  || filter === VersionFilter.Le;

export const getVersionLabel = (label: string) => {
  const title = <>
    <div><span style={{ color: '#0062FF' }}>包含模式：</span>支持选择多个版本，表示满足多个指定版本的数据</div>
    <div><span style={{ color: '#0062FF' }}>大于等于模式：</span>{'只能选择一个版本，表示 >= 指定版本的数据'}</div>
    <div><span style={{ color: '#0062FF' }}>小于等于模式：</span>{'只能选择一个版本，表示 <= 指定版本的数据'}</div>
  </>;

  return <Tooltip title={title}>
    {label} <QuestionCircleOutlined style={{ color: '#A3A3A9', fontSize: '14px' }} />
  </Tooltip>;
};

export const TOP_RULES = [{
  required: false,
  pattern: new RegExp(/^([1-9]|[1-9][0-9]|[1-9][0-9][0-9]|[1-2][0-9][0-9][0-9]|3000)$/, 'g'),
  message: '请输入整数且最大不超过3000,若输入超过3000,则默认按3000处理',
}];

export const TOP_NUM_TIPS = '用于指定查看按上报量排序的TopN的问题，最大不超过3000';

export const topNumLabel = <Tooltip title={'用于指定查看按上报量排序的TopN的问题，最大不超过3000'}>
  Top问题数 <QuestionCircleOutlined style={{ color: '#A3A3A9', fontSize: '14px' }} />
</Tooltip>;

export const getShowSwitchStatus = (appVersion?: AppVersionType) => {
  if (!appVersion) return false;

  const { values, operator } = appVersion;

  if (operator === 'OR' && Array.isArray(values) && !values.includes(FIELD_ALL) && values.length === 1) {
    return true;
  }

  return false;
};

export const onChangeAppVersion = (
  appVersion: AppVersionType | undefined,
  setShowSwitch: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  if (!appVersion) return;
  setShowSwitch(getShowSwitchStatus(appVersion));
};

export const getCachedFilters = (key: string) => {
  try {
    return JSON.parse(localStorage.getItem(key) as string);
  } catch (e) {
    return null;
  }
};

export const setCachedFilters = (key: string, filters: string[]) => {
  try {
    localStorage.setItem(key, JSON.stringify(filters));
  } catch (e) {
    logger.error(e);
  }
};

export const getValueFromEvent = (args: string[], mode: 'multiple' | 'tags' | 'single') => {
  if (mode === 'single') {
    return args;
  }

  if (args.indexOf(FIELD_ALL) === 0 && args.length > 1) {
    return args.slice(1);
  }

  if (args.some(val => val === FIELD_ALL)) {
    return [FIELD_ALL];
  }

  return args;
};

export const getValueFromEventOption = (args: Option[] | ExOption[], mode: 'multiple' | 'tags' | 'single') => {
  if (mode === 'single') {
    return args;
  }

  if (args.map(item => item.value).indexOf(FIELD_ALL) === 0 && args.length > 1) {
    return args.slice(1);
  }

  const all = args.find(item => item.value === FIELD_ALL);
  if (all) {
    return [all];
  }
  return args;
};

export const useOptions = (props: {
  fieldKey?: FieldKey,
  fieldScope?: FieldScope,
  userOptions?: Option[] | ExOption[],
}) => {
  const { fieldKey, fieldScope, userOptions } = props;
  const [options, setOptions] = useState<Option[] | ExOption[]>(userOptions || []);
  const { fields, loadField } = useStores().FieldStore;

  const load = async () => {
    if (userOptions) {
      setOptions(userOptions || []);
    } else if (fieldKey && fieldScope) {
      let field = getLocalField(fieldKey);
      if (field) {
        setOptions(field);
      } else {
        // 提前展示默认文案
        setOptions([{ value: FIELD_ALL, label: '所有' }]);
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        setTimeout(async () => {
          // 大量表单加载并发抢占网路队列，降低优先级（优先token、problemList加载）
          field = await loadField(fieldKey, fieldScope);
          setOptions(field);
        });
      }
    }
  };

  useEffect(() => {
    load();
  }, [fields, fieldKey, fieldScope, userOptions]);

  return options;
};

export const getFilterValue = (value: Record<string, any>) => {
  const snakeCaseValues = toSnakeCase(value);
  const timeRange = snakeCaseValues[FieldKey.TimeWindow] === TimeType.DAY
    || (!snakeCaseValues[FieldKey.AccurateDate] && snakeCaseValues[FieldKey.TimeRange])
    ? snakeCaseValues[FieldKey.TimeRange]
    : [snakeCaseValues[FieldKey.AccurateDate], snakeCaseValues[FieldKey.AccurateDate]];
  let reOperator = snakeCaseValues[FieldKey.AppVersionOperator];
  const getValues = () => {
    if (reOperator === AppVersionOperator.Between) {
      const minAppVersion = snakeCaseValues[FieldKey.MinAppVersion];
      const maxAppVersion = snakeCaseValues[FieldKey.MaxAppVersion];
      return minAppVersion && maxAppVersion ? `${minAppVersion},${maxAppVersion}` : undefined;
    }
    if (reOperator === AppVersionOperator.Wildcard) {
      const wildcardAppVersion = snakeCaseValues[FieldKey.WildcardAppVersion];
      reOperator = getWildcardOperator(wildcardAppVersion ?? '', false);

      return wildcardAppVersion?.replaceAll('*', '') ?? '';
    }
    return snakeCaseValues[FieldKey.AppVersion];
  };


  return {
    ...snakeCaseValues,
    [FieldKey.AppVersion]: {
      values: toArray(getValues()),
      operator: reOperator,
    },
    [FieldKey.TimeRange]: timeRange,
  };
};

export const getPgIdByIssueType = (issueType: IssueType, entry?: FilterEntry, isList = false) => {
  const memLeakDetailPgId = entry === FilterEntry.MemoryVcLeakDetail || entry === FilterEntry.MemoryVcLeakAnalysis
    ? PAGE_ID.memory.vcLeak.detail : PAGE_ID.memory.javaLeak.detail;
  const memLeakListPgId = entry === FilterEntry.MemoryVcLeakList
    ? PAGE_ID.memory.vcLeak.list : PAGE_ID.memory.javaLeak.list;
  const pgIdMap = {
    [IssueType.ISSUE_TYPE_CRASH]: isList ? PAGE_ID.crash.list : PAGE_ID.crash.detail,
    [IssueType.ISSUE_TYPE_ERROR]: isList ? PAGE_ID.error.list : PAGE_ID.error.detail,
    [IssueType.ISSUE_TYPE_FOOM_BUGLY]: isList ? PAGE_ID.foom.issues.list : PAGE_ID.foom.issues.detail,
    [IssueType.ISSUE_TYPE_ANR_BUGLY]: isList ? PAGE_ID.anr.list : PAGE_ID.anr.detail,
    [IssueType.ISSUE_TYPE_LAG]: isList ? PAGE_ID.dropFrame.list : PAGE_ID.dropFrame.detail,
    [IssueType.ISSUE_TYPE_MEM_LEAK]: isList ? memLeakListPgId : memLeakDetailPgId,
    [IssueType.ISSUE_TYPE_FD_LEAK]: isList ? PAGE_ID.memory.fd.list : PAGE_ID.memory.fd.detail,
    [IssueType.ISSUE_TYPE_NATIVE_MEM]: isList ? PAGE_ID.memory.native.list : PAGE_ID.memory.native.detail,
    [IssueType.ISSUE_TYPE_BITMAP]: isList ? PAGE_ID.memory.bigPic.list : PAGE_ID.memory.bigPic.detail,
    [IssueType.ISSUE_TYPE_BADMALLOC]: isList ? PAGE_ID.memory.badMalloc.list : PAGE_ID.memory.badMalloc.detail,
    [IssueType.ISSUE_TYPE_FOOM]: isList ? PAGE_ID.foom.issues.list : PAGE_ID.foom.issues.detail,
    [IssueType.ISSUE_TYPE_FOOM_MALLOC]: isList ? PAGE_ID.foom.malloc.list : PAGE_ID.foom.malloc.detail,
    [IssueType.ISSUE_TYPE_ANR]: isList ? PAGE_ID.anr.list : PAGE_ID.anr.detail,
    [IssueType.ISSUE_TYPE_MEM_GRAPH]: isList ? PAGE_ID.memory.memGraph.list : PAGE_ID.memory.memGraph.detail,
    [IssueType.ISSUE_TYPE_ELECTRON_CRASH]: isList ? PAGE_ID.electron.crashList : PAGE_ID.electron.crashDetail,
  };

  return get(pgIdMap, issueType) || '';
};

// 堆栈类型筛选项分词匹配模式下，对输入字符串分词处理
export const tokenizeValue = (value: any) => {
  if (value?.mode === MatchMode.TOKENIZATION || value?.mode === MatchMode.NOTTOKENIZATION) {
    const nextValue = value?.values?.join('\n').split(/[^a-zA-Z0-9]/) ?? [];
    return {
      mode: value.mode,
      values: nextValue?.filter((item: any) => !!item),
    };
  }
  return value;
};


// 过滤表单没有value值的字段
export const filterFormValues = (
  formItems: FormEntry[],
  formValues: Record<string, any>,
) => formItems?.filter((entry) => {
  if (isNil(formValues?.[String(entry?.name)])) {
    return false;
  }
  switch (entry.type) {
    case FormType.TimeRange:
      return !isNil(formValues?.[String(entry?.name)]);
    case FormType.TimeRangeCascade:
      return !isNil(formValues?.[String(entry?.name)]);
    case FormType.ProDatePicker:
      return !isNil(formValues?.[String(entry?.name)]?.value) && !isNil(formValues?.[String(entry?.name)]?.dateRange);
    case FormType.CharacterInput:
      return !isNil(formValues?.[String(entry?.name)]?.values)
        && checkFilterList(formValues?.[String(entry?.name)]?.values);
    case FormType.Input:
      return !isNil(formValues?.[String(entry?.name)]);
    case FormType.Select:
      return  (Array.isArray(formValues?.[String(entry?.name)])
        && checkFilterList(formValues?.[String(entry?.name)]))
        || (!Array.isArray(formValues?.[String(entry?.name)]) && !isNil(formValues?.[String(entry?.name)]));
    case FormType.SelectGroup:
      return !isNil(formValues?.[String(entry?.name)])
        && checkFilterList(formValues?.[String(entry?.name)]);
    case FormType.SelectInputGroup:
      return !isNil(formValues?.[String(entry?.name)]);
    case FormType.SelectNumberGroup:
      return !isNil(formValues?.[String(entry?.name)]?.min)
        || !isNil(formValues?.[String(entry?.name)]?.max)
        || formValues?.[String(entry?.name)]?.length || checkFilterList(formValues?.[String(entry?.name)]?.value);
    case FormType.Switch:
      return !isNil(formValues?.[String(entry?.name)]);
    case FormType.RadioGroup:
      return !isNil(formValues?.[String(entry?.name)]);
    case FormType.VersionSelect:
      return !isNil(formValues?.[String(entry?.name)]?.values)
        && checkFilterList(formValues?.[String(entry?.name)]?.values);
    case FormType.VersionSelectGroup:
      return !isNil(formValues?.[String(entry?.name)]?.version);
    case FormType.DetailSelectGroup:
      return !isNil(formValues?.[String(entry?.name)]);
    case FormType.CustomFields:
      return !isNil(formValues?.[String(entry?.name)]) && formValues?.[String(entry?.name)]?.length;
    case FormType.CustomKeys:
      return !isNil(formValues?.[String(entry?.name)]) && formValues?.[String(entry?.name)]?.length;
    case FormType.BuglyCustomKeys:
      return !isNil(formValues?.[String(entry?.name)]);
    case FormType.AutoComplete:
      return !isNil(formValues?.[String(entry?.name)]);
    case FormType.DateTimeRange:
      return !isNil(formValues?.[String(entry?.name)]);
    case FormType.Rules:
      return !isNil(formValues?.[String(entry?.name)]) && checkFilterList(formValues?.[String(entry?.name)]);
    default:
      return false;
  }
})?.map?.(item => String(item.name));


const checkFilterList = (list: any[]) => list?.filter?.((item: any) => item !== '')?.length > 0;
